
import { isEmpty } from "lodash";
import {
  IAccount,
  INavigation,
  IUserWallet,
  LEAD_TIME_FIELD_NAME,
  PAYMENT_BUTTONS_PREF_FIELD_NAME,
  RSI_HOME_NAMES,
} from "@share/common-types";
import { LIFE_STYLE_NAME_PARAM, SESSION_KEY_TOKEN_NAME_PARAM } from "../constants/common";
import { UrlUtils } from "./urls";
import { NULL_VALUE } from "@constants";
import { LoginType } from "./login";

export const getPreferenceFromAccount = (account: IAccount | null | undefined, key: string, defaultValue?: string) => {
  if (!isEmpty(account?.preferences)) {
    const pref = account?.preferences.find(p => p.key === key);
    if (!isEmpty(pref?.value)) {
      return pref?.value;
    }
  }
  return !isEmpty(defaultValue) ? defaultValue : NULL_VALUE;
}

export const getPreferenceFileUrlFromAccount = (account: IAccount | null | undefined, key: string) => {
  const preference =  getPreferenceFromAccount(account, key);
  if (!isEmpty(preference)) {
    try {
      const preferenceObj = preference ? JSON.parse(preference) : preference;
      if (!isEmpty(preferenceObj?.Value)) {
        return preferenceObj.Value;
      }
    } catch (e) {
      console.error('Parsing JSON error')
    }
  }
  return NULL_VALUE;
}


export const getAccountLeadTime = (account: IAccount) => {
  const leadTimePref = getPreferenceFromAccount(account, LEAD_TIME_FIELD_NAME, '0');
    
  let leadTime = 0;
  try {
    leadTime = leadTimePref ? parseInt(leadTimePref) : 0;
  } catch(e) {
    leadTime = 0;
    console.error(e);
  }
  return leadTime;
}

export const IsClientCashEnabled = (account: IAccount | null | undefined) => {
  const isPublicAccountType = account?.type === LoginType.Public;
  const isPrivateWithLoginAccountType = account?.type === LoginType.PrivateWithLogin;
  const hasClientCash = account?.hasClientCash;

  return (isPublicAccountType || isPrivateWithLoginAccountType) && hasClientCash;
}

const GetPrivateTokenParam = (account: IAccount | null | undefined) => {
  const isPrivateWithTokenAccountType = account?.type === LoginType.PrivateWithToken;
  const privateTokenFromUrl = account?.privateTokenFromUrl;
  const privateTokenParam = `${(isPrivateWithTokenAccountType && privateTokenFromUrl) ? `${SESSION_KEY_TOKEN_NAME_PARAM}=${UrlUtils.getSearchParam(SESSION_KEY_TOKEN_NAME_PARAM)}` : ''}`;

  return privateTokenParam;
}

const GetLifeStyleParam = (account: IAccount | null | undefined) => {
  const hasClientCash = account?.hasClientCash;
  const lifeStyle = UrlUtils.getSearchParam(LIFE_STYLE_NAME_PARAM);
  const lifeStyleParam = `${(hasClientCash && !isEmpty(lifeStyle)) ? `${LIFE_STYLE_NAME_PARAM}=${lifeStyle}` : ''}`;

  return lifeStyleParam;
}

export const GetHomeParams = (account: IAccount | null | undefined) => {
  const privateTokenParam = GetPrivateTokenParam(account);
  const lifeStyleParam = GetLifeStyleParam(account);

  const hasPrivateTokenParam = !isEmpty(privateTokenParam);
  const hasLifeStyleParam = !isEmpty(lifeStyleParam);
  
  if (hasPrivateTokenParam && hasLifeStyleParam) {
    return `?${privateTokenParam}&${lifeStyleParam}`;
  } else if (hasPrivateTokenParam) {
    return `?${privateTokenParam}`;
  } else if (hasLifeStyleParam) {
    return `?${lifeStyleParam}`;
  } else {
    return '';
  }
}

export const GetWalletNumber = (value: number | null | undefined, account: IAccount | null | undefined) => {
  const balance = (value || value === 0) ? account?.walletNoDecimals ? Math.floor(value || 0) : Math.floor((value || 0) * 100) / 100 : null;
  return balance?.toLocaleString('en-US');
}

export const IsCapitalVacationsAccount = (account: IAccount | null | undefined) => {
  return (
    !!account?.isCapitalVacations ||
    !!account?.isCapitalVacationsMain ||
    !!account?.isCapitalVacationsAgent ||
    !!account?.isCapitalVacationsCondoRes
  );
}

// Capital's wallet holds points rather than money, and so does any wallet whose points are not
// worth exactly one unit of currency — prefixing those balances with "$" misreads the number by
// orders of magnitude. A rate that has not resolved yet falls back to money so a money balance
// never flashes without its symbol while the wallet loads.
export const IsPointsWallet = (
  account: IAccount | null | undefined,
  userWalletData: IUserWallet | null | undefined,
) => {
  const convertionRate = userWalletData?.convertionRate;

  return IsCapitalVacationsAccount(account) || (!!convertionRate && convertionRate !== 1);
}

// `unit` is the wallet's client-cash name (e.g. "Points"), and it is spelled out after the number
// on Capital only (TRAV23-4707): their owners were reading the unprefixed balance as dollars. Every
// other points wallet keeps the bare number it has always shown, so the unit is ignored there.
export const GetWalletAmount = (
  value: number | null | undefined,
  account: IAccount | null | undefined,
  userWalletData: IUserWallet | null | undefined,
  unit?: string | null | undefined,
) => {
  const balance = GetWalletNumber(value, account);

  if (balance === undefined) {
    return '-';
  }

  if (!IsPointsWallet(account, userWalletData)) {
    return `$${balance}`;
  }

  return (IsCapitalVacationsAccount(account) && !isEmpty(unit)) ? `${balance} ${unit}` : balance;
}

export const GetStyles = (
    account: IAccount | null | undefined,
    backgroundImage: any,
    borderRadius: string | null | undefined,
    rsiBackground: any,
    defaultBackground: any,
    isSearch: boolean,
    isExperiences: boolean | null | undefined,
    isWidget: boolean | null | undefined,
    widgetBackgroundColor: string | null | undefined,
    listLength: number | null | undefined) => {
  let withImage = false;
  const styleAccount: any = {};   
  const styleMain: any = {};   
  if (!isExperiences) {  
    if (isWidget && widgetBackgroundColor) {
      styleAccount.background = widgetBackgroundColor;
      styleMain.background = 'none';
    } else {
      const background = account?.backgroundColor;
      const logoImage = backgroundImage ? 
                          backgroundImage : 
                          RSI_HOME_NAMES.includes(account?.name || '')? 
                              rsiBackground : 
                              background ? 
                                  null : 
                                  isWidget ? null : defaultBackground;

      if (background && !logoImage) {
        styleAccount.background = background;
        styleMain.background = 'none';
      } 

      if (logoImage) {
        if (!isSearch) {
          withImage = true;
          styleMain.background = 'none';
        }

        styleAccount.backgroundImage = account?.isRSITemplate || listLength ?
          !isSearch && `url(${logoImage})` :
          isWidget ?
            !isSearch && `url(${logoImage})` :
            !isSearch && `linear-gradient(rgba(255,255,255,0) 80%, rgba(255,255,255,1)), url(${logoImage})`;
      }  
    }
  } 

  if (borderRadius && !isEmpty(borderRadius)) {
    try {
      const border = parseInt(borderRadius);
      styleAccount.borderRadius = `${border}px`;
    } catch(e) {
      console.error(e);
    }
  }

  return { styleAccount, styleMain, withImage };
}

const normalizeRecentSearchBlocklistIds = (raw?: (string | number)[] | null): string[] => {
  if (!raw?.length) return [];
  return raw
    .map((id) => String(id).trim())
    .filter((s) => s.length > 0);
};

const parseContextId = (id: number | string | null | undefined): string | null => {
  if (id == null) return null;
  const s = String(id).trim();
  return s.length > 0 ? s : null;
};

/**
 * Returns true when the recent-search preview image grid should be hidden for this org/site.
 * RSI only; `disableRecentSearchPreviewImagesRsi` turns previews off for everyone; org/site lists
 * are blocklists (only matching IDs are hidden). Each list is evaluated only when non-empty, with
 * normalized string ids so CSV/whitespace mismatches do not hide the feature incorrectly.
 */
export const computeIsRecentSearchPreviewImagesDisabled = (
  account: IAccount | null | undefined,
  siteId?: number | string | null,
  organizationId?: number | string | null,
): boolean => {
  const isDisabled = !!account?.disableRecentSearchPreviewImagesRsi;
  if (!isDisabled) {
    return false;
  }

  if (!account?.isRSITemplate) {
    return isDisabled;
  }
  
  const siteList = normalizeRecentSearchBlocklistIds(account?.disableRecentSearchPreviewImagesRsiSiteidList);
  const orgList = normalizeRecentSearchBlocklistIds(account?.disableRecentSearchPreviewImagesRsiOrgidList);

  if (siteList?.length === 0 && orgList?.length === 0) {
    return true;
  }

  const sid = parseContextId(siteId);
  const oid = parseContextId(organizationId);
  const excludedBySite = siteList?.length > 0 && sid != null && siteList.includes(sid);
  const excludedByOrg = orgList?.length > 0 && oid != null && orgList.includes(oid);

  return excludedBySite || excludedByOrg;
};

export const GetDisableNewTabOpen = (account: IAccount | null, items: INavigation | null) => {
  const isRSITemplate = account?.isRSITemplate;
  const disableNewTabOpenRsiSiteidList = account?.disableNewTabOpenRsiSiteidList;
  const disableNewTabOpenRsiOrgidList = account?.disableNewTabOpenRsiOrgidList;

  const siteId: any = items?.siteId;
  const organizationId: any = items?.organizationId;

  const emptyDisableNewTabOpenRsiSiteid = !disableNewTabOpenRsiSiteidList?.length;
  const emptyDisableNewTabOpenRsiOrgid = !disableNewTabOpenRsiOrgidList?.length;
  const emptyDisableNewTabOpen = emptyDisableNewTabOpenRsiSiteid && emptyDisableNewTabOpenRsiOrgid;

  const includedDisableNewTabOpenRsiSiteid = !emptyDisableNewTabOpenRsiSiteid ? disableNewTabOpenRsiSiteidList?.includes(siteId?.toString()) : false;
  const includedDisableNewTabOpenRsiOrgid = !emptyDisableNewTabOpenRsiOrgid ? disableNewTabOpenRsiOrgidList?.includes(organizationId?.toString()) : false;

  const disableNewTabOpenRsi = isRSITemplate && ((account?.disableNewTabOpen && emptyDisableNewTabOpen) || (account?.disableNewTabOpen && (includedDisableNewTabOpenRsiSiteid || includedDisableNewTabOpenRsiOrgid)))
  const disableNewTabOpenRest = !isRSITemplate && account?.disableNewTabOpen
  const disableNewTabOpen = disableNewTabOpenRsi || disableNewTabOpenRest;

  return disableNewTabOpen;
}

/** Credit line: preference on + private login/token only (not Public or API). */
export const isEnableCreditLine = (account: IAccount | null | undefined): boolean =>
  Boolean(account?.enableCreditLine) &&
  (account?.type === LoginType.PrivateWithLogin ||
    account?.type === LoginType.PrivateWithToken);

/** Alias for checkout UI and balance API eligibility. */
export const isCreditLineCheckoutAccount = (account: IAccount | null | undefined): boolean =>
  isEnableCreditLine(account);

/** Stripe express checkout: non-RSI uses account toggle only; RSI scopes by org/site lists (empty = all). */
export const computeIsStripePaymentMethodsEnabled = (
  account: IAccount | null | undefined,
  siteId?: number | string | null,
  organizationId?: number | string | null,
): boolean => {
  if (isEnableCreditLine(account)) {
    return false;
  }
  if (!account?.enableStripePaymentMethods) {
    return false;
  }
  if (!account.isRSITemplate) {
    return true;
  }
  const orgList = account.enableStripePaymentMethodsRsiOrgidList ?? [];
  const siteList = account.enableStripePaymentMethodsRsiSiteidList ?? [];
  if (!orgList.length && !siteList.length) {
    return true;
  }
  const sid = siteId != null ? String(siteId) : '';
  const oid = organizationId != null ? String(organizationId) : '';
  if (!sid && !oid) {
    return false;
  }
  return siteList.includes(sid) || orgList.includes(oid);
};

/** True if Stripe ExpressCheckoutElement's onReady `availablePaymentMethods` includes any wallet button. */
export const hasStripeExpressWalletMethods = (
  available: Record<string, boolean> | null | undefined,
): boolean => {
  if (!available || typeof available !== 'object') {
    return false;
  }
  return Object.values(available).some((v) => v === true);
};

/** The allowed payment-button keys for this account ("google_pay", "apple_pay", "link", ...).
 *  Sourced from `account.paymentButtons` if populated; otherwise parsed from the
 *  `payment_buttons` account preference (comma-separated). Empty array means "no wallet picker
 *  configured" — callers should hide the method-selection radio and render the card form only. */
export const getAccountPaymentButtonKeys = (account: IAccount | null | undefined): string[] => {
  if (!account) return [];
  if (account.paymentButtons && account.paymentButtons.length > 0) {
    return account.paymentButtons;
  }
  const pref = account.preferences?.find((p) => p.key === PAYMENT_BUTTONS_PREF_FIELD_NAME);
  const raw = pref?.value;
  return raw
    ? String(raw).split(',').map((s: string) => s.trim()).filter(Boolean)
    : [];
};

/** Convenience: true when the account has no `payment_buttons` preference configured, so the
 *  payment-method radio picker should be hidden and the card form rendered on its own. */
export const isPaymentButtonsPrefEmpty = (account: IAccount | null | undefined): boolean => {
  return isEmpty(getAccountPaymentButtonKeys(account));
};

/* ---------------------------------------------------------------------------
 * Extra Stripe payment methods (TRAV23-4982 sync from the activities front).
 * Additive; nothing above is changed.
 * ------------------------------------------------------------------------- */

/** `payment_buttons` tokens that Stripe's ExpressCheckoutElement CAN render as a wallet button,
 *  beyond the original five. Klarna is the only one — see {@link PAYMENT_ELEMENT_ONLY_METHODS}.
 *  Treated exactly like Google/Apple/Link: probe-gated row, single-method ExpressCheckoutElement,
 *  Stripe's own branded button and popup (TRAV23-4560). */
export const EXPRESS_CHECKOUT_EXTRA_METHODS = ['klarna'];

/** `payment_buttons` tokens the ExpressCheckoutElement cannot render at all — Stripe's
 *  `ExpressCheckoutPaymentMethodsOption` has no key for either, so there is no express button to
 *  show. They are offered through a `<PaymentElement>` pinned to the single method instead. */
export const PAYMENT_ELEMENT_ONLY_METHODS = ['afterpay_clearpay', 'cashapp'];

/** Currencies each PaymentElement-only method accepts. Stripe validates `payment_method_types`
 *  against the currency when the intent is created, so offering one of these outside its currency
 *  list does not degrade gracefully — it errors. Source: Stripe "Payment method support".
 *  Klarna is absent: it goes through the ExpressCheckoutElement, whose eligibility probe already
 *  filters by currency and country, and `card` is unrestricted. */
const PAYMENT_ELEMENT_METHOD_CURRENCIES: Record<string, string[]> = {
  afterpay_clearpay: ['aud', 'cad', 'eur', 'nzd', 'gbp', 'usd'],
  // Cash App Pay is USD / US customers only.
  cashapp: ['usd'],
};

/** True when `method` can be offered for `currency`. Unknown methods (e.g. `card`) are unrestricted. */
export const isPaymentElementMethodAllowedForCurrency = (
  method: string,
  currency: string | null | undefined,
): boolean => {
  const allowed = PAYMENT_ELEMENT_METHOD_CURRENCIES[method];
  if (!allowed) return true;
  return allowed.includes((currency ?? 'usd').toLowerCase());
};

/** PaymentElement-only methods this account has enabled AND that Stripe accepts for `currency`, in a
 *  stable display order. Each becomes a picker row backed by an inline PaymentElement. */
export const getPaymentElementExtraMethods = (
  paymentButtonKeys: string[] | null | undefined,
  currency?: string | null,
): string[] => {
  if (!paymentButtonKeys?.length) return [];
  return PAYMENT_ELEMENT_ONLY_METHODS.filter(
    (m) => paymentButtonKeys.includes(m) && isPaymentElementMethodAllowedForCurrency(m, currency),
  );
};

/** ExpressCheckoutElement methods this account has enabled beyond the original five wallets. Like the
 *  wallets, these are additionally gated by Stripe's `onReady` eligibility probe, which is what
 *  applies the currency/country rules — so no currency list is needed here. */
export const getExpressCheckoutExtraMethods = (
  paymentButtonKeys: string[] | null | undefined,
): string[] => {
  if (!paymentButtonKeys?.length) return [];
  return EXPRESS_CHECKOUT_EXTRA_METHODS.filter((m) => paymentButtonKeys.includes(m));
};
